import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Check, ShoppingBag, ArrowLeft, ArrowRight, X, Info, ChevronLeft, ChevronRight, BadgeInfo, Plus, Minus } from 'lucide-react';
import Layout from '../Layout';
import { LoisirManager } from '../../helpers/loisir';
import { showToast } from '../../helpers/customToast';
import { getData } from '../../helpers/ApiSettings';
import { API_BASE_URL } from '../../helpers/ApiSettings';
import DatePicker from 'react-datepicker';
import { registerLocale } from "react-datepicker";
import fr from 'date-fns/locale/fr';
import 'react-datepicker/dist/react-datepicker.css';
import '../../css/loisir.css';

registerLocale('fr', fr);

const ProductDetailModal = ({ product, onClose }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  if (!product) return null;

  const hasMultipleImages = product.images && product.images.length > 1;

  const nextImage = () => {
    if (product.images && product.images.length > 0) {
      setActiveImageIndex((prevIndex) =>
        prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (product.images && product.images.length > 0) {
      setActiveImageIndex((prevIndex) =>
        prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
      );
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{product.title}</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            {product.images && product.images.length > 0 && (
              <div className="product-image-carousel">
                <div className="carousel-main-image">
                  <img
                    src={API_BASE_URL + product.images[activeImageIndex].url}
                    alt={`${product.title} - image ${activeImageIndex + 1}`}
                    className="img-fluid rounded mx-auto d-block mb-3"
                  />

                  {hasMultipleImages && (
                    <div className="carousel-controls">
                      <button className="carousel-control-prev" onClick={prevImage}>
                        <ChevronLeft size={24} />
                      </button>
                      <span className="carousel-counter">
                        {activeImageIndex + 1} / {product.images.length}
                      </span>
                      <button className="carousel-control-next" onClick={nextImage}>
                        <ChevronRight size={24} />
                      </button>
                    </div>
                  )}
                </div>

                {hasMultipleImages && (
                  <div className="carousel-thumbnails">
                    {product.images.map((image, index) => (
                      <div
                        key={index}
                        className={`carousel-thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                        onClick={() => setActiveImageIndex(index)}
                      >
                        <img
                          src={API_BASE_URL + image.url}
                          alt={`Thumbnail ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="mb-3">
              {/* {product.quantity && (
                <div className="mb-2">
                  <BadgeInfo size={20} className="me-2" />
                  <span><strong>Quantité disponible :</strong> {product.availableQuantity || product.quantity}</span>
                </div>
              )} */}
              {product.minimumAge && (
                <div>
                  <BadgeInfo size={20} className="me-2" />
                  <span><strong>Age minimum :</strong> {product.minimumAge}</span>
                </div>
              )}
            </div>
            
            <div className="product-description mt-3">
              <div dangerouslySetInnerHTML={{ __html: product.description }} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuantitySelector = ({ quantity, maxQuantity, onQuantityChange, disabled = false }) => {
  const decreaseQuantity = () => {
    if (quantity > 1) {
      onQuantityChange(quantity - 1);
    }
  };

  const increaseQuantity = () => {
    if (quantity < maxQuantity) {
      onQuantityChange(quantity + 1);
    }
  };

  return (
    <div className="quantity-selector d-flex align-items-center">
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={decreaseQuantity}
        disabled={disabled || quantity <= 1}
      >
        <Minus size={14} />
      </button>
      <span className="mx-2 fw-bold">{quantity}</span>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={increaseQuantity}
        disabled={disabled || quantity >= maxQuantity}
      >
        <Plus size={14} />
      </button>
    </div>
  );
};

const LoisirSelectionPage = () => {
  const navigate = useNavigate();
  const [formulas, setFormulas] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(1);
  const [detailProduct, setDetailProduct] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  // Selection state
  const [selectedFormula, setSelectedFormula] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]); // Now includes quantities: [{productId, product, quantity}]
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState({ price: 0, deposit: 0 });

  // Calculate pagination
  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = products.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(products.length / itemsPerPage);

  // Pagination functions
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Load formulas
        const formulasData = await LoisirManager.fetchFormulas();
        setFormulas(formulasData);

        // Load loisir products (without date filtering initially)
        const productsData = await LoisirManager.fetchLoisirProducts();
        setProducts(productsData);

        // Check if there's a saved selection
        const savedSelection = LoisirManager.getSelection();
        if (savedSelection) {
          setSelectedFormula(savedSelection.formula);
          setSelectedDuration(savedSelection.duration);
          setSelectedProducts(savedSelection.products || []);
          setStartDate(savedSelection.startDate ? new Date(savedSelection.startDate) : null);
          setEndDate(savedSelection.endDate ? new Date(savedSelection.endDate) : null);

          // Calculate price based on saved selection
          if (savedSelection.formula && savedSelection.duration) {
            const formula = formulasData.find(f => f.id === savedSelection.formula.id);
            if (formula) {
              const price = LoisirManager.calculatePrice(formula, savedSelection.duration);
              setCalculatedPrice(price);
            }
          }

          // Set the appropriate step
          if (savedSelection.products && savedSelection.products.length > 0) {
            setActiveStep(2);
          }
          if (savedSelection.startDate && savedSelection.endDate) {
            setActiveStep(3);
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        showToast('Erreur lors du chargement des données', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Update end date when start date or duration changes
  useEffect(() => {
    if (startDate && selectedDuration) {
      const newEndDate = new Date(startDate);
      newEndDate.setDate(startDate.getDate() + selectedDuration - 1);
      setEndDate(newEndDate);
    }
  }, [startDate, selectedDuration]);

  // Calculate price when formula or duration changes
  useEffect(() => {
    if (selectedFormula && selectedDuration) {
      const price = LoisirManager.calculatePrice(selectedFormula, selectedDuration);
      setCalculatedPrice(price);
    }
  }, [selectedFormula, selectedDuration]);

  // Refresh product availability when dates change
  useEffect(() => {
    const refreshProductAvailability = async () => {
      if (startDate && endDate && activeStep === 2) {
        setCheckingAvailability(true);
        try {
          const updatedProducts = await LoisirManager.fetchLoisirProducts(null, startDate.toISOString(), endDate.toISOString());
          setProducts(updatedProducts);
          
          // Check if any selected products are no longer available
          const unavailableProducts = selectedProducts.filter(selected => 
            !updatedProducts.find(p => p.id === selected.productId && p.availableQuantity >= selected.quantity)
          );
          
          if (unavailableProducts.length > 0) {
            showToast(`Certains articles sélectionnés ne sont plus disponibles pour ces dates`, 'warn');
            // Remove unavailable products from selection
            setSelectedProducts(prev => 
              prev.filter(selected => 
                updatedProducts.find(p => p.id === selected.productId && p.availableQuantity >= selected.quantity)
              )
            );
          }
        } catch (error) {
          console.error('Error refreshing product availability:', error);
        } finally {
          setCheckingAvailability(false);
        }
      }
    };

    refreshProductAvailability();
  }, [startDate, endDate]);

  // Save selection to sessionStorage when it changes
  useEffect(() => {
    if (selectedFormula || selectedProducts.length > 0 || startDate) {
      LoisirManager.saveSelection({
        formula: selectedFormula,
        duration: selectedDuration,
        products: selectedProducts,
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null
      });
    }
  }, [selectedFormula, selectedDuration, selectedProducts, startDate, endDate]);

  const handleFormulaSelect = (formula) => {
    setSelectedFormula(formula);
    // Clear selected products if the new formula allows fewer products
    const totalSelectedItems = selectedProducts.reduce((sum, item) => sum + item.quantity, 0);
    if (totalSelectedItems > formula.article_quantity) {
      setSelectedProducts([]);
      showToast(`Vous pouvez sélectionner jusqu'à ${formula.article_quantity} articles avec cette formule`, 'info');
    }
  };

  const handleProductSelect = (product) => {
    if (!selectedFormula) {
      showToast('Veuillez d\'abord sélectionner une formule', 'warn');
      return;
    }

    const existingSelection = selectedProducts.find(p => p.productId === product.id);
    const totalSelectedItems = selectedProducts.reduce((sum, item) => sum + item.quantity, 0);
    
    if (existingSelection) {
      // Remove product if already selected
      setSelectedProducts(prev => prev.filter(p => p.productId !== product.id));
    } else {
      // Add product if under the formula limit
      if (totalSelectedItems < selectedFormula.article_quantity) {
        const maxQuantity = Math.min(
          product.availableQuantity || product.quantity || 1,
          selectedFormula.article_quantity - totalSelectedItems
        );
        setSelectedProducts(prev => [...prev, {
          productId: product.id,
          product: product,
          quantity: 1,
          maxQuantity: maxQuantity
        }]);
      } else {
        showToast(`Vous ne pouvez pas sélectionner plus de ${selectedFormula.article_quantity} articles avec cette formule`, 'warn');
      }
    }
  };

  const handleQuantityChange = (productId, newQuantity) => {
    setSelectedProducts(prev => prev.map(item => {
      if (item.productId === productId) {
        const totalOtherItems = prev.reduce((sum, other) => 
          other.productId !== productId ? sum + other.quantity : sum, 0
        );
        const maxAllowed = Math.min(
          item.maxQuantity,
          selectedFormula.article_quantity - totalOtherItems
        );
        
        return {
          ...item,
          quantity: Math.min(newQuantity, maxAllowed)
        };
      }
      return item;
    }));
  };

  const isProductSelected = (productId) => {
    return selectedProducts.some(p => p.productId === productId);
  };

  const getSelectedProductQuantity = (productId) => {
    const selected = selectedProducts.find(p => p.productId === productId);
    return selected ? selected.quantity : 0;
  };

  const getTotalSelectedItems = () => {
    return selectedProducts.reduce((sum, item) => sum + item.quantity, 0);
  };

  const isSelectionComplete = () => {
    switch (activeStep) {
      case 1:
        return selectedFormula && selectedDuration;
      case 2:
        return selectedProducts.length > 0;
      case 3:
        return startDate && endDate;
      default:
        return false;
    }
  };

  const handleNextStep = () => {
    if (isSelectionComplete()) {
      if (activeStep === 3) {
        // Final step - proceed to checkout
        navigate('/loisirs/checkout');
      } else {
        setActiveStep(prev => prev + 1);
      }
    } else {
      let message = 'Veuillez compléter votre sélection avant de continuer';
      if (activeStep === 1) {
        message = 'Veuillez sélectionner une formule et une durée';
      } else if (activeStep === 2) {
        message = 'Veuillez sélectionner au moins un article';
      } else if (activeStep === 3) {
        message = 'Veuillez sélectionner une date de début';
      }
      showToast(message, 'warn');
    }
  };

  const handlePreviousStep = () => {
    if (activeStep > 1) {
      setActiveStep(prev => prev - 1);
    }
  };

  const showProductDetail = (product, e) => {
    e.stopPropagation();
    setDetailProduct(product);
  };

  const closeProductDetail = () => {
    setDetailProduct(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container my-5 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des données...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container my-5">
        <h1 className="mb-4">Sélection de votre formule Loisirs</h1>

        {/* Progress Steps */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="progress-steps">
              <div className={`progress-step ${activeStep >= 1 ? 'active' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-label">Formule</div>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${activeStep >= 2 ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-label">Articles</div>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${activeStep >= 3 ? 'active' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-label">Dates</div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Select Formula and Duration */}
        {activeStep === 1 && (
          <>
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Étape 1: Choisissez votre formule</h3>
                <div className="row mt-4">
                  {formulas.map((formula) => (
                    <div key={formula.id} className="col-md-4 mb-4">
                      <div
                        className={`card h-100 ${selectedFormula?.id === formula.id ? 'border-primary' : ''}`}
                        onClick={() => handleFormulaSelect(formula)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-body text-center">
                          <h5 className="card-title">{formula.name}</h5>
                          <p className="card-text">
                            {formula.article_quantity} articles au choix
                          </p>
                          {selectedFormula?.id === formula.id && (
                            <div className="selected-badge">
                              <Check size={18} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Choisissez la durée</h3>
                <div className="row mt-4">
                  {selectedFormula && selectedFormula.components_pricing_formula_prices
                    .sort((a, b) => a.duration_days - b.duration_days)
                    .map((priceItem) => {
                      const durationDays = priceItem.duration_days;
                      const isMonthly = durationDays === 30;

                      return (
                        <div key={durationDays} className="col-md-4 mb-3">
                          <div
                            className={`card h-100 ${selectedDuration === durationDays ? 'border-primary' : ''}`}
                            onClick={() => setSelectedDuration(durationDays)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="card-body text-center">
                              <h5 className="card-title">
                                {isMonthly ? 'Abonnement mensuel' : `${durationDays} jours`}
                              </h5>
                              {isMonthly && (
                                <p className="text-muted mb-1">({durationDays} jours renouvelables)</p>
                              )}
                              <p className="card-text">
                                {priceItem.price.toFixed(2)}€{isMonthly ? '/mois' : ''}
                              </p>
                              {selectedDuration === durationDays && (
                                <div className="selected-badge">
                                  <Check size={18} />
                                </div>
                              )}
                            </div>
                            {isMonthly && (
                              <div className="card-footer bg-success bg-opacity-10 text-center py-2">
                                <small className="text-success">
                                  <Check size={14} className="me-1" /> Avantages exclusifs
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            </div>
          </>
        )}

        {/* Step 2: Select Products */}
        {activeStep === 2 && (
          <div className="row">
            <div className="col-md-8">
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="card-title mb-0">Étape 2: Choisissez vos articles</h3>
                    {checkingAvailability && (
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Vérification...</span>
                      </div>
                    )}
                  </div>
                  <p className="card-text">
                    Sélectionnez jusqu'à {selectedFormula?.article_quantity} articles pour votre formule {selectedFormula?.name}.
                    {startDate && endDate && (
                      <span className="text-muted">
                        <br />Les quantités affichées sont disponibles du {startDate.toLocaleDateString('fr-FR')} au {endDate.toLocaleDateString('fr-FR')}.
                      </span>
                    )}
                  </p>

                  {/* Compact product grid */}
                  <div className="row g-2">
                    {currentProducts.map(product => {
                      const isSelected = isProductSelected(product.id);
                      const selectedQuantity = getSelectedProductQuantity(product.id);
                      const availableQuantity = product.availableQuantity || product.quantity || 1;
                      const totalOtherItems = selectedProducts.reduce((sum, item) => 
                        item.productId !== product.id ? sum + item.quantity : sum, 0
                      );
                      const maxSelectable = Math.min(
                        availableQuantity,
                        selectedFormula.article_quantity - totalOtherItems
                      );

                      return (
                        <div key={product.id} className="col-6 col-md-3 mb-2">
                          <div
                            className={`card h-100 product-card-compact ${isSelected ? 'border-primary' : ''} ${availableQuantity === 0 ? 'opacity-50' : ''}`}
                            onClick={() => availableQuantity > 0 && handleProductSelect(product)}
                            style={{ cursor: availableQuantity > 0 ? 'pointer' : 'not-allowed' }}
                          >
                            {product.images && product.images[0] && (
                              <div className="product-image-container">
                                <img
                                  src={API_BASE_URL + product.images[0].url}
                                  className="product-image-compact"
                                  alt={product.title}
                                />
                                {availableQuantity === 0 && (
                                  <div className="position-absolute top-50 start-50 translate-middle">
                                    <span className="badge bg-danger">Indisponible</span>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="card-body p-2">
                              <h6 className="product-title-compact">{product.title}</h6>
                              <div className="d-flex justify-content-between align-items-center">
                                {/* <small className="text-muted">
                                  Stock: {availableQuantity}
                                </small> */}
                                <button
                                  className="btn btn-sm btn-info detail-btn"
                                  onClick={(e) => showProductDetail(product, e)}
                                >
                                  <Info size={14} />
                                </button>
                              </div>
                              
                              {isSelected && (
                                <>
                                  <div className="selected-badge">
                                    <Check size={16} />
                                  </div>
                                  <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                                    <QuantitySelector
                                      quantity={selectedQuantity}
                                      maxQuantity={maxSelectable}
                                      onQuantityChange={(newQuantity) => handleQuantityChange(product.id, newQuantity)}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination controls */}
                  {totalPages > 1 && (
                    <div className="pagination-controls d-flex justify-content-center mt-4">
                      <button
                        className="btn btn-outline-secondary me-2"
                        onClick={prevPage}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span className="pagination-info align-self-center">
                        Page {currentPage} sur {totalPages}
                      </span>
                      <button
                        className="btn btn-outline-secondary ms-2"
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card mb-4 sticky-top" style={{ top: '20px' }}>
                <div className="card-body">
                  <h4 className="card-title">Votre sélection</h4>
                  <p className="mb-3">
                    <strong>Formule:</strong> {selectedFormula?.name}<br />
                    <strong>Durée:</strong> {selectedDuration} jours<br />
                    <strong>Prix:</strong> {calculatedPrice.price.toFixed(2)}€<br />
                    <strong>Caution:</strong> {calculatedPrice.deposit.toFixed(2)}€
                  </p>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Articles sélectionnés</h5>
                    <span className="badge bg-primary">
                      {getTotalSelectedItems()}/{selectedFormula?.article_quantity}
                    </span>
                  </div>

                  {selectedProducts.length === 0 ? (
                    <p className="text-muted">Aucun article sélectionné</p>
                  ) : (
                    <ul className="list-group mt-3">
                      {selectedProducts.map(item => (
                        <li key={item.productId} className="list-group-item d-flex justify-content-between align-items-center py-2">
                          <div className="text-truncate me-2" style={{ maxWidth: '60%' }}>
                            {item.product.title}
                            {item.quantity > 1 && <span className="text-muted"> (x{item.quantity})</span>}
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductSelect(item.product);
                            }}
                          >
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Select Dates */}
        {activeStep === 3 && (
          <div className="row">
            <div className="col-md-8">
              <div className="card loisir-card mb-4">
                <div className="card-body">
                  <h3 className="card-title">Étape 3: Choisissez vos dates</h3>
                  <p className="card-text">
                    Sélectionnez la date de début pour votre location de {selectedDuration} jours.
                  </p>

                  <div className="row mt-4">
                    <div className="col-md-6">
                      <label className="form-label">Date de début</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text">
                          <Calendar size={18} />
                        </span>
                        <DatePicker
                          selected={startDate}
                          onChange={date => setStartDate(date)}
                          minDate={new Date(new Date().setDate(new Date().getDate() + 2))}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Sélectionnez une date"
                          locale="fr"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Date de fin (calculée automatiquement)</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text">
                          <Calendar size={18} />
                        </span>
                        <DatePicker
                          selected={endDate}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          disabled
                          locale="fr"
                        />
                      </div>
                    </div>
                  </div>

                  {checkingAvailability && (
                    <div className="alert alert-info">
                      <div className="d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Vérification de la disponibilité pour ces dates...
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card mb-4">
                <div className="card-body">
                  <h4 className="card-title">Récapitulatif</h4>
                  <div className="mb-3">
                    <strong>Formule:</strong> {selectedFormula?.name}<br />
                    <strong>Durée:</strong> {selectedDuration} jours<br />
                    <strong>Prix:</strong> {calculatedPrice.price.toFixed(2)}€<br />
                    <strong>Caution:</strong> {calculatedPrice.deposit.toFixed(2)}€
                  </div>

                  <h5>Articles sélectionnés ({getTotalSelectedItems()})</h5>
                  <ul className="list-group mt-3 mb-4 selected-products-list">
                    {selectedProducts.map(item => (
                      <li key={item.productId} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>{item.product.title}</span>
                        {item.quantity > 1 && <span className="badge bg-secondary">{item.quantity}</span>}
                      </li>
                    ))}
                  </ul>

                  {startDate && endDate && (
                    <div className="mb-3">
                      <h5>Dates de location</h5>
                      <p>
                        Du {startDate.toLocaleDateString('fr-FR')} au {endDate.toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="d-flex justify-content-between mt-4">
          <button
            className="btn btn-outline-secondary"
            onClick={handlePreviousStep}
            disabled={activeStep === 1}
          >
            <ArrowLeft size={18} className="me-2" />
            Retour
          </button>

          <button
            className="btn btn-primary"
            onClick={handleNextStep}
            disabled={checkingAvailability}
          >
            {activeStep === 3 ? (
              <>
                Procéder au paiement
                <ShoppingBag size={18} className="ms-2" />
              </>
            ) : (
              <>
                Continuer
                <ArrowRight size={18} className="ms-2" />
              </>
            )}
          </button>
        </div>

        {/* Product Detail Modal */}
        {detailProduct && (
          <ProductDetailModal
            product={detailProduct}
            onClose={closeProductDetail}
          />
        )}
      </div>
    </Layout>
  );
};

export default LoisirSelectionPage;